/**
 * G21 variant resolver.
 * PDF pages 1 and 4: preserve Shopify product form while hiding Veil visually and resolving
 * the real available variant before native events. No stock text or variant ID is fabricated.
 */
(() => {
  'use strict';
  const N = (window.G21Perfect ||= {});
  if (N.variants) return;

  const PICKER = 'variant-picker';
  const HIDDEN = 'fieldset[hidden][data-g21-hidden-option="true"], fieldset[data-g21-hidden-option="true"]';

  const ensureSpanishSizeStyle = () => {
    if (document.getElementById('g21-spanish-product-compat-style')) return;
    const style = document.createElement('style');
    style.id = 'g21-spanish-product-compat-style';
    style.textContent = '.g21-product-page variant-picker fieldset[data-g21-option-group="Talla"] > .variant-option__button-label{min-width:54px!important;padding-inline:11px!important;}';
    document.head.append(style);
  };

  const matrix = (controller) => {
    if (!controller.productData?.variants?.length) {
      controller.productData = N.safeJson?.(controller.section.querySelector('[data-g21-product-data]'));
    }
    return controller.productData;
  };

  const groups = (picker) => Array.from(picker.querySelectorAll('fieldset[data-g21-option-group], fieldset'))
    .filter((fieldset) => fieldset.querySelector('input[type="radio"], select'));

  const groupName = (controller, fieldset, index) =>
    fieldset.dataset.optionName || fieldset.querySelector('legend')?.textContent?.trim() || matrix(controller)?.optionNames?.[index] || '';

  const normalizeLocalizedGroups = (controller, picker) => {
    ensureSpanishSizeStyle();
    groups(picker).forEach((group, index) => {
      const name = groupName(controller, group, index).trim().toLowerCase();
      if (name === 'velo' || name.includes('velo ')) {
        group.dataset.g21HiddenOption = 'true';
        group.hidden = true;
        group.setAttribute('aria-hidden', 'true');
      }
    });
  };

  const selectedValue = (fieldset) =>
    fieldset.querySelector('input[type="radio"]:checked')?.value || fieldset.querySelector('select')?.value || '';

  const setValue = (fieldset, value) => {
    const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    const radio = radios.find((input) => input.value === value && !input.disabled);
    if (radio) {
      if (radio.checked) return { changed: false, control: radio };
      radios.forEach((input) => {
        input.checked = input === radio;
        input.toggleAttribute('data-current-checked', input === radio);
      });
      return { changed: true, control: radio };
    }
    const select = fieldset.querySelector('select');
    if (select && Array.from(select.options).some((option) => option.value === value && !option.disabled)) {
      if (select.value === value) return { changed: false, control: select };
      select.value = value;
      return { changed: true, control: select };
    }
    return { changed: false, control: null };
  };

  const updateTrust = (controller, variant) => {
    const stock = controller.section.querySelector('[data-g21-stock]');
    if (!stock) return;
    const available = Boolean(variant?.available);
    stock.classList.toggle('is-available', available);
    stock.innerHTML = `<span aria-hidden="true">●</span> ${available ? stock.dataset.inStockText : stock.dataset.outOfStockText}`;
  };

  const syncNativeState = (controller, chosen) => {
    if (!chosen?.available || !chosen.id) return;

    controller.productData.selectedVariantId = chosen.id;
    controller.section.querySelectorAll('product-form-component').forEach((component) => {
      const input = component.querySelector('input[name="id"], input[ref="variantId"]');
      if (input instanceof HTMLInputElement) {
        input.value = String(chosen.id);
        input.setAttribute('value', String(chosen.id));
      }

      component.querySelectorAll('add-to-cart-component').forEach((container) => {
        if (typeof container.enable === 'function') container.enable();
        const button = container.querySelector('button[ref="addToCartButton"], button[type="submit"]');
        if (button instanceof HTMLButtonElement) {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
        }
      });

      component.querySelectorAll('[ref="acceleratedCheckoutButtonContainer"], shopify-accelerated-checkout').forEach((element) => {
        element.removeAttribute('hidden');
      });
    });

    const sticky = controller.section.querySelector('sticky-add-to-cart');
    if (sticky) {
      sticky.setAttribute('data-variant-available', 'true');
      sticky.setAttribute('data-current-variant-id', String(chosen.id));
      const button = sticky.querySelector('button[ref="addToCartButton"]');
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  };

  const resolveOne = (controller, picker, { requestNativeUpdate = false } = {}) => {
    if (controller.variantSyncGuard) return null;
    const data = matrix(controller);
    if (!data?.variants?.length) return null;

    normalizeLocalizedGroups(controller, picker);
    const optionGroups = groups(picker);
    const hiddenIndexes = optionGroups.map((group, index) => ({
      group, index, name: groupName(controller, group, index).toLowerCase()
    })).filter(({ group, name }) => group.matches(HIDDEN) || /veil|duvak|velo/.test(name));
    if (!hiddenIndexes.length) return null;

    const selected = optionGroups.map(selectedValue);
    const visibleIndexes = optionGroups.map((group, index) => ({ group, index }))
      .filter(({ group, index }) => !hiddenIndexes.some((hidden) => hidden.index === index) && !group.hidden)
      .map(({ index }) => index);

    const candidates = data.variants.filter((variant) =>
      variant.available && visibleIndexes.every((index) => !selected[index] || variant.options[index] === selected[index])
    );
    if (!candidates.length) {
      const fallback = data.variants.find((variant) => variant.available);
      if (!fallback) return null;
      candidates.push(fallback);
    }

    let chosen = candidates.find((variant) => hiddenIndexes.every(({ index }) =>
      !selected[index] || variant.options[index] === selected[index]
    ));
    chosen ||= candidates.find((variant) => hiddenIndexes.every(({ index }) =>
      /complimentary|complementary|plain|free|cortesía|cortesia|gratis|liso/i.test(variant.options[index] || '')
    ));
    chosen ||= candidates[0];

    let changedControl = null;
    controller.variantSyncGuard = true;
    try {
      hiddenIndexes.forEach(({ group, index }) => {
        const result = setValue(group, chosen.options[index]);
        if (result.changed) changedControl = result.control;
      });
    } finally { controller.variantSyncGuard = false; }

    updateTrust(controller, chosen);
    syncNativeState(controller, chosen);
    const serverDiffers = String(data.selectedVariantId || '') !== String(chosen.id || '');
    if (!changedControl && serverDiffers) {
      changedControl = hiddenIndexes.map(({ group }) => group.querySelector('input[type="radio"]:checked, select')).find(Boolean)
        || picker.querySelector('input[type="radio"]:checked, select');
    }
    if (changedControl && requestNativeUpdate) {
      queueMicrotask(() => changedControl.isConnected && changedControl.dispatchEvent(new Event('change', { bubbles: true })));
    }
    return chosen;
  };

  N.variants = {
    resolveAll(controller, options) {
      controller.section.querySelectorAll(PICKER).forEach((picker) => resolveOne(controller, picker, options));
    },
    onChangeCapture(controller, event) {
      if (controller.variantSyncGuard || !(event.target instanceof Element)) return;
      const picker = event.target.closest(PICKER);
      if (!picker || !controller.section.contains(picker)) return;
      normalizeLocalizedGroups(controller, picker);
      if (!event.target.closest(HIDDEN)) resolveOne(controller, picker, { requestNativeUpdate: false });
      controller.scheduleMeasure();
    },
    selections(controller) {
      const picker = controller.section.querySelector(PICKER);
      if (!picker) return {};
      normalizeLocalizedGroups(controller, picker);
      const result = {};
      groups(picker).forEach((group, index) => {
        if (group.hidden || group.matches(HIDDEN)) return;
        const name = groupName(controller, group, index);
        const value = selectedValue(group);
        if (name && value) result[name] = value;
      });
      return result;
    }
  };
})();

/**
 * G21 unpublished-theme preview guard.
 * Shopify localization POSTs can redirect to a market subdomain and lose preview_theme_id,
 * which makes the browser fall back to the published theme. Preserve preview parameters
 * in return_to without affecting normal storefront visits.
 */
(() => {
  'use strict';

  const pageUrl = new URL(window.location.href);
  const previewThemeId = pageUrl.searchParams.get('preview_theme_id');
  if (!previewThemeId) return;

  const previewKeys = ['preview_theme_id', '_fd', 'pb', 'preview_key'];

  const buildReturnTo = () => {
    const returnUrl = new URL(window.location.href);
    previewKeys.forEach((key) => {
      const value = pageUrl.searchParams.get(key);
      if (value) returnUrl.searchParams.set(key, value);
    });
    return `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
  };

  const patchLocalizationForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/localization')) return;

    let returnInput = form.querySelector('input[name="return_to"]');
    if (!returnInput) {
      returnInput = document.createElement('input');
      returnInput.type = 'hidden';
      returnInput.name = 'return_to';
      form.append(returnInput);
    }
    returnInput.value = buildReturnTo();
  };

  const patchAll = (scope = document) => {
    scope.querySelectorAll?.('form[action*="/localization"]').forEach(patchLocalizationForm);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => patchAll(), { once: true });
  } else {
    patchAll();
  }

  document.addEventListener('change', (event) => {
    const form = event.target instanceof Element
      ? event.target.closest('form[action*="/localization"]')
      : null;
    if (form) patchLocalizationForm(form);
  }, true);

  document.addEventListener('submit', (event) => {
    patchLocalizationForm(event.target);
  }, true);

  document.addEventListener('shopify:section:load', (event) => {
    patchAll(event.target);
  });
})();
